// Clean version of initPromptLibrary.js without fallback prompts
import Papa from 'papaparse';

// Storage keys
const SYSTEM_PROMPTS_KEY = 'blue_comet_system_prompts';
const SYSTEM_PROMPTS_INITIALIZED = 'system_prompts_initialized';

// Initialize the prompt library from the CSV file
export const initializeFromCsv = async (forceReload = false) => {
  try {
    // Check if already initialized to avoid duplicate initialization
    if (localStorage.getItem(SYSTEM_PROMPTS_INITIALIZED) === 'true' && !forceReload) {
      console.log('System prompts already initialized');
      return true;
    }

    console.log('Fetching prompt library CSV...');
    
    // Use the correct path for public directory content - it's served from root
    // Add cache-busting to prevent browser caching
    const response = await fetch('/promptlibrary.csv?v=' + Date.now());
    
    if (!response.ok) {
      throw new Error(`Failed to fetch CSV: ${response.status} ${response.statusText}`);
    }
    
    const csvText = await response.text();
    console.log(`Fetched CSV: ${csvText.length} bytes`);
    
    if (!csvText || csvText.trim().length === 0) {
      throw new Error('CSV file is empty');
    }
    
    // Split by lines to check content
    const lines = csvText.split('\n').filter(line => line.trim().length > 0);
    console.log(`CSV has ${lines.length} non-empty lines`);
    
    // Check first line for headers
    const firstLine = lines[0];
    console.log('First line:', firstLine);
    
    // Check if first line contains expected headers
    const hasHeaders = firstLine.includes('tag') && 
                       firstLine.includes('name') && 
                       firstLine.includes('content');
    
    console.log('CSV appears to have headers:', hasHeaders);
    
    // Parse the CSV with appropriate settings
    const parseResult = Papa.parse(csvText, {
      header: hasHeaders,           // Use headers if detected
      skipEmptyLines: true,         // Skip empty lines
      transformHeader: h => h.trim() // Trim whitespace from headers
    });
    
    if (parseResult.errors.length > 0) {
      console.warn('CSV parsing had errors:', parseResult.errors);
    }
    
    console.log(`Parsed ${parseResult.data.length} rows from CSV`);
    
    // Map CSV data to prompt objects
    const systemPrompts = [];
    
    // Process each row
    parseResult.data.forEach((row, index) => {
      // Skip header row if we're parsing without headers and first line has headers
      if (index === 0 && !hasHeaders && firstLine.includes('tag') && firstLine.includes('name')) {
        return;
      }
      
      // Get data based on whether we have headers
      let tag, name, content;
      
      if (hasHeaders) {
        // Using column names
        tag = row.tag?.trim();
        name = row.name?.trim();
        content = row.content?.trim();
      } else {
        // Using array indices (assuming order is: tag, name, content)
        tag = Array.isArray(row) ? row[0]?.trim() : '';
        name = Array.isArray(row) ? row[1]?.trim() : '';
        content = Array.isArray(row) ? row[2]?.trim() : '';
      }
      
      // Skip rows without required data
      if (!name || !content) {
        console.log(`Skipping row ${index} due to missing name or content`);
        return;
      }
      
      // Generate tags array
      const tags = ['system'];
      
      // Add the main tag if it exists
      if (tag) {
        // Clean up the tag (lowercase, replace spaces with hyphens)
        const cleanTag = tag.toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
        
        if (cleanTag && !tags.includes(cleanTag)) {
          tags.push(cleanTag);
        }
      }
      
      // Add content-based tags
      if (content.toLowerCase().includes('python')) tags.push('python');
      if (content.toLowerCase().includes('code') || 
          content.toLowerCase().includes('function')) tags.push('coding');
      if (content.toLowerCase().includes('algorithm')) tags.push('algorithm');
      if (content.toLowerCase().includes('data')) tags.push('data');
      
      // Create the prompt object
      systemPrompts.push({
        id: `system-${index}`,
        name: name,
        content: content,
        tags: tags,
        createdAt: Date.now() - (Math.random() * 30 * 24 * 60 * 60 * 1000),
        lastUsedAt: null,
        useCount: 0,
        isSystemPrompt: true
      });
    });
    
    console.log(`Created ${systemPrompts.length} system prompts from CSV`);
    
    // If no valid prompts were found, throw an error
    if (systemPrompts.length === 0) {
      throw new Error('No valid prompts could be extracted from CSV');
    }
    
    // Store in localStorage
    localStorage.setItem(SYSTEM_PROMPTS_KEY, JSON.stringify(systemPrompts));
    localStorage.setItem(SYSTEM_PROMPTS_INITIALIZED, 'true');
    
    // Dispatch an event that other components can listen for
    window.dispatchEvent(new CustomEvent('systemPromptsLoaded', {
      detail: { count: systemPrompts.length }
    }));
    
    return true;
  } catch (error) {
    console.error('Error initializing prompt library from CSV:', error);
    console.error('Path tried: /promptlibrary.csv');
    console.error('Make sure the CSV file exists in your public folder');
    
    // No fallback prompts - just return false to indicate failure
    return false;
  }
};

// Get system prompts from localStorage
export const getSystemPrompts = () => {
  try {
    const storedPrompts = localStorage.getItem(SYSTEM_PROMPTS_KEY);
    if (!storedPrompts) {
      console.warn('No system prompts found in localStorage');
      return [];
    }
    
    const parsedPrompts = JSON.parse(storedPrompts);
    if (!parsedPrompts || !Array.isArray(parsedPrompts)) {
      console.error('Invalid system prompts data in localStorage');
      return [];
    }
    
    console.log(`Retrieved ${parsedPrompts.length} system prompts from storage`);
    return parsedPrompts;
  } catch (error) {
    console.error('Error loading system prompts:', error);
    return [];
  }
};

// Record usage of a system prompt
export const recordSystemPromptUsage = (id) => {
  try {
    const prompts = getSystemPrompts();
    const index = prompts.findIndex(p => p.id === id);
    
    if (index === -1) return null;
    
    prompts[index] = {
      ...prompts[index],
      lastUsedAt: Date.now(),
      useCount: (prompts[index].useCount || 0) + 1
    };
    
    localStorage.setItem(SYSTEM_PROMPTS_KEY, JSON.stringify(prompts));
    return prompts[index];
  } catch (error) {
    console.error('Error recording system prompt usage:', error);
    return null;
  }
};

// Force reinitialization of system prompts
export const resetSystemPrompts = async () => {
  console.log('Resetting system prompts...');
  localStorage.removeItem(SYSTEM_PROMPTS_KEY);
  localStorage.removeItem(SYSTEM_PROMPTS_INITIALIZED);
  
  // Now force a reload
  return await initializeFromCsv(true);
};